import { apiClient } from '@/shared/api';

import type {
  CreateStoreLocationRequest,
  CreateStoreRegionRequest,
  StoreLocationListResponse,
  StoreLocationPageResponse,
  StoreLocationResponse,
  StoreLocationSearchRequest,
  StoreRegionListResponse,
  StoreRegionLocationMappingResponse,
  StoreRegionPageResponse,
  StoreRegionResponse,
  StoreRegionSearchRequest,
  StoreServiceSettingsResponse,
  UpdateStoreLocationRequest,
  UpdateStoreRegionRequest,
  UpdateStoreServiceSettingsRequest,
} from '../model/settingsTypes';

const BASE = '/api/v1/store';

export const searchStoreLocations = async (
  body: StoreLocationSearchRequest,
): Promise<StoreLocationPageResponse> =>
  (await apiClient.post<StoreLocationPageResponse>(`${BASE}/locations/search`, body)).data;

export const createStoreLocation = async (
  body: CreateStoreLocationRequest,
): Promise<StoreLocationResponse> =>
  (await apiClient.post<StoreLocationResponse>(`${BASE}/locations`, body)).data;

export const updateStoreLocation = async (
  id: string,
  body: UpdateStoreLocationRequest,
): Promise<StoreLocationResponse> =>
  (await apiClient.put<StoreLocationResponse>(`${BASE}/locations/${id}`, body)).data;

export const deleteStoreLocation = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/locations/${id}`);
};

export const activateStoreLocation = async (id: string): Promise<StoreLocationResponse> =>
  (await apiClient.post<StoreLocationResponse>(`${BASE}/locations/${id}/activate`)).data;

export const deactivateStoreLocation = async (id: string): Promise<StoreLocationResponse> =>
  (await apiClient.post<StoreLocationResponse>(`${BASE}/locations/${id}/deactivate`)).data;

export const findNearbyStoreLocations = async (params: {
  latitude: number;
  longitude: number;
  radiusKm?: number;
  limit?: number;
}): Promise<StoreLocationListResponse> =>
  (await apiClient.get<StoreLocationListResponse>(`${BASE}/locations/nearby`, { params })).data;

export const getStoreRegionsByLocation = async (
  locationId: string,
): Promise<StoreRegionListResponse> =>
  (await apiClient.get<StoreRegionListResponse>(`${BASE}/locations/${locationId}/regions`)).data;

export const searchStoreRegions = async (
  body: StoreRegionSearchRequest,
): Promise<StoreRegionPageResponse> =>
  (await apiClient.post<StoreRegionPageResponse>(`${BASE}/regions/search`, body)).data;

export const createStoreRegion = async (
  body: CreateStoreRegionRequest,
): Promise<StoreRegionResponse> =>
  (await apiClient.post<StoreRegionResponse>(`${BASE}/regions`, body)).data;

export const updateStoreRegion = async (
  id: string,
  body: UpdateStoreRegionRequest,
): Promise<StoreRegionResponse> =>
  (await apiClient.put<StoreRegionResponse>(`${BASE}/regions/${id}`, body)).data;

export const deleteStoreRegion = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/regions/${id}`);
};

export const activateStoreRegion = async (id: string): Promise<StoreRegionResponse> =>
  (await apiClient.post<StoreRegionResponse>(`${BASE}/regions/${id}/activate`)).data;

export const deactivateStoreRegion = async (id: string): Promise<StoreRegionResponse> =>
  (await apiClient.post<StoreRegionResponse>(`${BASE}/regions/${id}/deactivate`)).data;

export const getStoreLocationsByRegion = async (
  regionId: string,
): Promise<StoreLocationListResponse> =>
  (await apiClient.get<StoreLocationListResponse>(`${BASE}/regions/${regionId}/locations`)).data;

export const assignStoreLocationToRegion = async (
  regionId: string,
  locationId: string,
): Promise<StoreRegionLocationMappingResponse> =>
  (
    await apiClient.post<StoreRegionLocationMappingResponse>(
      `${BASE}/regions/${regionId}/locations/${locationId}`,
    )
  ).data;

export const removeStoreLocationFromRegion = async (
  regionId: string,
  locationId: string,
): Promise<void> => {
  await apiClient.delete(`${BASE}/regions/${regionId}/locations/${locationId}`);
};

export const getStoreServiceSettings = async (): Promise<StoreServiceSettingsResponse> =>
  (await apiClient.get<StoreServiceSettingsResponse>(`${BASE}/store-settings/current`)).data;

export const updateStoreServiceSettings = async (
  body: UpdateStoreServiceSettingsRequest,
): Promise<StoreServiceSettingsResponse> =>
  (await apiClient.put<StoreServiceSettingsResponse>(`${BASE}/store-settings/current`, body)).data;
